/**
 * ParticleBackground - WebGL particle animation background
 * Inspired by Yuri Artiukh (Akella)
 * Adapted for React + Three.js
 */
import { useEffect, useRef } from 'react';
import * as THREE from 'three';

interface CameraSettings {
  position: { x: number; y: number; z: number };
  lookAt: { x: number; y: number; z: number };
  fov: number;
}

interface ParticleBackgroundProps {
  speed?: number;
  particleCount?: { x: number; z: number };
  waveAmplitude?: number;
  noiseIntensity?: number;
  camera?: CameraSettings;
}

// Default camera: nearly top-down view
const DEFAULT_CAMERA: CameraSettings = {
  position: { x: 0, y: 15, z: 10 },
  lookAt: { x: 0, y: 0, z: 15 },
  fov: 83,
};

// Login camera: flatter, more dramatic angle
export const LOGIN_CAMERA: CameraSettings = {
  position: { x: 0, y: 6, z: -5 },
  lookAt: { x: 0, y: 0, z: 20 },
  fov: 75,
};

// Route-specific cameras: subtle variations with slight rotations
// Base reference: dashboard at y:15, z:10, fov:83
// Variations go "in" (closer) rather than "out" (further)
export const ROUTE_CAMERAS: Record<string, CameraSettings> = {
  // Dashboard: centered reference
  dashboard: {
    position: { x: 0, y: 15, z: 10 },
    lookAt: { x: 0, y: 0, z: 15 },
    fov: 83,
  },
  // Customers: slight right tilt, closer
  customers: {
    position: { x: 1.5, y: 13.5, z: 11 },
    lookAt: { x: 1, y: 0, z: 16 },
    fov: 84,
  },
  // Invoices: slight left tilt, closer
  invoices: {
    position: { x: -1.5, y: 13, z: 11.5 },
    lookAt: { x: -1, y: 0, z: 15 },
    fov: 85,
  },
  // Contracts: slight right, closer in
  contracts: {
    position: { x: 2, y: 12.5, z: 12 },
    lookAt: { x: 1.5, y: 0, z: 16 },
    fov: 86,
  },
  // Estimates: left tilt, slightly closer
  estimates: {
    position: { x: -2, y: 14, z: 11 },
    lookAt: { x: -1.5, y: 0, z: 15.5 },
    fov: 84,
  },
  // Settings: centered, closer
  settings: {
    position: { x: 0, y: 12, z: 12 },
    lookAt: { x: 0, y: 0, z: 16 },
    fov: 87,
  },
  // Animation: much closer, dramatic angle to showcase particles
  animation: {
    position: { x: 3, y: 8, z: 14 },
    lookAt: { x: 0, y: 0, z: 18 },
    fov: 78,
  },
};

export function ParticleBackground({
  speed = 0.002,
  particleCount = { x: 80, z: 40 },
  waveAmplitude = 2.5,
  noiseIntensity = 1.2,
  camera: cameraProp,
}: ParticleBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const particleMaterialRef = useRef<THREE.ShaderMaterial | null>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const speedRef = useRef(speed);

  // Camera refs - initialized once, updated via second useEffect
  const targetCameraRef = useRef<CameraSettings | null>(null);
  const currentCameraRef = useRef<CameraSettings | null>(null);

  // Initialize camera refs on first render only
  if (targetCameraRef.current === null) {
    const initial = cameraProp || DEFAULT_CAMERA;
    targetCameraRef.current = {
      position: { ...initial.position },
      lookAt: { ...initial.lookAt },
      fov: initial.fov
    };
    currentCameraRef.current = {
      position: { ...initial.position },
      lookAt: { ...initial.lookAt },
      fov: initial.fov
    };
  }

  // Keep speed ref updated
  speedRef.current = speed;

  useEffect(() => {
    if (!canvasRef.current) return;

    // Setup renderer
    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: false,
      alpha: true,
    });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 0); // Transparent
    rendererRef.current = renderer;

    // Setup camera with initial settings from refs
    const initialCamera = currentCameraRef.current!;

    const camera = new THREE.PerspectiveCamera(
      initialCamera.fov,
      window.innerWidth / window.innerHeight,
      0.1,
      500
    );
    camera.position.set(
      initialCamera.position.x,
      initialCamera.position.y,
      initialCamera.position.z
    );
    camera.lookAt(
      initialCamera.lookAt.x,
      initialCamera.lookAt.y,
      initialCamera.lookAt.z
    );
    cameraRef.current = camera;

    // Setup scene
    const scene = new THREE.Scene();
    scene.position.y = 0;
    sceneRef.current = scene;

    // Generate particles
    const numberOfParticles = {
      x: particleCount.x,
      z: particleCount.z,
      halfX: particleCount.x / 2,
      total: particleCount.x * particleCount.z,
    };

    const particleGeometry = new THREE.BufferGeometry();
    const particleVertices = new Float32Array(numberOfParticles.total * 3);
    const particleColors = new Float32Array(numberOfParticles.total * 3);
    const particleSizes = new Float32Array(numberOfParticles.total);

    // Colors: Subtle silver + matte dark grey
    const particleFirstColor = [0.7, 0.7, 0.75]; // Silver
    const particleSecondColor = [0.25, 0.25, 0.28]; // Matte dark grey

    let k = -1;
    const r = waveAmplitude;
    for (let i = -numberOfParticles.halfX; i < numberOfParticles.halfX; i++) {
      for (let j = 0; j < numberOfParticles.z; j++) {
        k++;

        // Grid layout with wave - spread to fill screen
        const x = i * 1.0;
        const y =
          Math.cos((i / numberOfParticles.x) * Math.PI) * r +
          Math.sin((j / numberOfParticles.z) * Math.PI) * r * 0.5;
        const z = j * 1.0;

        particleVertices.set([x, y, z], k * 3);

        // Size variation based on grid pattern
        const isAccent = (j % 8 === 0) || (i % 6 === 0);
        particleSizes[k] = isAccent ? 10 : 5;

        // Random accent colors
        const color = Math.random() > 0.92 ? particleFirstColor : particleSecondColor;
        particleColors.set(color, k * 3);
      }
    }

    particleGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(particleVertices, 3)
    );
    particleGeometry.setAttribute(
      'color',
      new THREE.BufferAttribute(particleColors, 3)
    );
    particleGeometry.setAttribute(
      'size',
      new THREE.BufferAttribute(particleSizes, 1)
    );

    // Custom shader material
    const particleMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uNoiseIntensity: { value: noiseIntensity },
      },
      blending: THREE.AdditiveBlending,
      depthTest: false,
      transparent: true,
      vertexShader: `
        varying vec3 vColor;
        uniform float uTime;
        uniform float uNoiseIntensity;
        attribute vec3 color;
        attribute float size;

        // Classic Perlin 2D Noise by Stefan Gustavson
        vec4 permute(vec4 x) { return mod(((x*34.0)+1.0)*x, 289.0); }
        vec2 fade(vec2 t) { return t*t*t*(t*(t*6.0-15.0)+10.0); }

        float cnoise(vec2 P) {
          vec4 Pi = floor(P.xyxy) + vec4(0.0, 0.0, 1.0, 1.0);
          vec4 Pf = fract(P.xyxy) - vec4(0.0, 0.0, 1.0, 1.0);
          Pi = mod(Pi, 289.0);
          vec4 ix = Pi.xzxz;
          vec4 iy = Pi.yyww;
          vec4 fx = Pf.xzxz;
          vec4 fy = Pf.yyww;
          vec4 i = permute(permute(ix) + iy);
          vec4 gx = 2.0 * fract(i * 0.0243902439) - 1.0;
          vec4 gy = abs(gx) - 0.5;
          vec4 tx = floor(gx + 0.5);
          gx = gx - tx;
          vec2 g00 = vec2(gx.x, gy.x);
          vec2 g10 = vec2(gx.y, gy.y);
          vec2 g01 = vec2(gx.z, gy.z);
          vec2 g11 = vec2(gx.w, gy.w);
          vec4 norm = 1.79284291400159 - 0.85373472095314 *
            vec4(dot(g00, g00), dot(g01, g01), dot(g10, g10), dot(g11, g11));
          g00 *= norm.x;
          g01 *= norm.y;
          g10 *= norm.z;
          g11 *= norm.w;
          float n00 = dot(g00, vec2(fx.x, fy.x));
          float n10 = dot(g10, vec2(fx.y, fy.y));
          float n01 = dot(g01, vec2(fx.z, fy.z));
          float n11 = dot(g11, vec2(fx.w, fy.w));
          vec2 fade_xy = fade(Pf.xy);
          vec2 n_x = mix(vec2(n00, n01), vec2(n10, n11), fade_xy.x);
          float n_xy = mix(n_x.x, n_x.y, fade_xy.y);
          return 2.3 * n_xy;
        }

        void main() {
          vColor = color;

          vec3 newPosition = position;

          // More movement: multiple noise layers
          float t = uTime * 0.5;
          newPosition.x += sin(position.y * 0.3 + t) * 2.5;
          newPosition.y += cnoise(position.xy * 0.08 + t) * uNoiseIntensity;
          newPosition.y += cnoise(position.xz * 0.06 + t * 1.3) * uNoiseIntensity * 0.8;
          newPosition.z += cos(position.x * 0.2 + t * 0.7) * 1.5;

          gl_PointSize = size;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
        }
      `,
      fragmentShader: `
        varying vec3 vColor;

        void main() {
          // Round particles
          vec2 xy = gl_PointCoord.xy - vec2(0.5);
          float ll = length(xy);
          float alpha = step(ll, 0.5) * 0.6; // Reduced opacity for subtlety

          gl_FragColor = vec4(vColor, alpha);
        }
      `,
    });
    particleMaterialRef.current = particleMaterial;

    // Create particle mesh
    const particleMesh = new THREE.Points(particleGeometry, particleMaterial);
    scene.add(particleMesh);

    // Lerp helper
    const lerp = (start: number, end: number, t: number) => start + (end - start) * t;

    // Animation loop
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      if (particleMaterialRef.current) {
        particleMaterialRef.current.uniforms.uTime.value += speedRef.current;
      }

      // Smooth camera animation
      const cam = cameraRef.current;
      const target = targetCameraRef.current;
      const current = currentCameraRef.current;

      if (cam && target && current) {
        const lerpSpeed = 0.025; // Smooth transition speed

        // Lerp position
        current.position.x = lerp(current.position.x, target.position.x, lerpSpeed);
        current.position.y = lerp(current.position.y, target.position.y, lerpSpeed);
        current.position.z = lerp(current.position.z, target.position.z, lerpSpeed);

        // Lerp lookAt
        current.lookAt.x = lerp(current.lookAt.x, target.lookAt.x, lerpSpeed);
        current.lookAt.y = lerp(current.lookAt.y, target.lookAt.y, lerpSpeed);
        current.lookAt.z = lerp(current.lookAt.z, target.lookAt.z, lerpSpeed);

        // Lerp FOV
        current.fov = lerp(current.fov, target.fov, lerpSpeed);

        // Apply to camera
        cam.position.set(current.position.x, current.position.y, current.position.z);
        cam.lookAt(current.lookAt.x, current.lookAt.y, current.lookAt.z);
        cam.fov = current.fov;
        cam.updateProjectionMatrix();
      }

      renderer.render(scene, camera);
    };
    animate();

    // Handle window resize
    const handleResize = () => {
      if (!camera || !renderer) return;

      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      particleGeometry.dispose();
      particleMaterial.dispose();
      renderer.dispose();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [particleCount.x, particleCount.z, waveAmplitude, noiseIntensity]);

  // Update target camera when prop changes (triggers smooth animation)
  useEffect(() => {
    const newTarget = cameraProp || DEFAULT_CAMERA;
    targetCameraRef.current = {
      position: { ...newTarget.position },
      lookAt: { ...newTarget.lookAt },
      fov: newTarget.fov
    };
  }, [cameraProp]);

  return (
    <canvas
      ref={canvasRef}
      id="particle-background"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        // A0: dvh instead of % — on mobile the visual viewport shrinks and
        // grows with the URL bar; % left a black band below the fold.
        height: '100dvh',
        zIndex: -1,
        pointerEvents: 'none',
      }}
    />
  );
}

export default ParticleBackground;
